import styled from 'styled-components';

const Section = styled.section.attrs({ 'data-broadcast-queue': '' })`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  margin: 0;
  cursor: default;
  color: ${(props) => props.theme.colors.text};
`;

const AccordionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
`;

const AccordionPanel = styled.div`
  width: 100%;
  background: ${(props) => props.theme.colors.widget_background};
  box-sizing: border-box;
  border-radius: 10px;
  overflow: hidden;

  & + & {
    margin-top: 8px;
  }
`;

const ItemHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  min-height: 60px;
  box-sizing: border-box;

  @media (min-width: 600px) {
    flex-wrap: nowrap;
    justify-content: space-between;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const PlayIconWrap = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${(props) => props.theme.colors.outline};
  color: ${(props) => props.theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlayButton = styled.button`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: ${(props) => props.theme.colors.outline};
  color: ${(props) => props.theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  &:hover {
    opacity: 0.9;
  }
  &:focus {
    outline: none;
  }
`;

const HeaderTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const HeaderFilename = styled.span`
  font-size: 14px;
  line-height: 1.35;
  color: ${(props) => props.theme.colors.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderSummary = styled.span`
  font-size: 12px;
  line-height: 1.35;
  color: ${(props) => props.theme.colors.inactive};
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  width: 100%;

  @media (min-width: 600px) {
    width: auto;
  }
`;

const SearchWrap = styled.div`
  flex: 1;
  min-width: 120px;
  max-width: 200px;

  @media (min-width: 600px) {
    width: 220px;
    max-width: 220px;
  }

  /* Scoped to this wrapper only; does not affect inputs elsewhere */
  & input {
    width: 100%;
    box-sizing: border-box;
  }
`;

const SearchInputWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 40px;
  min-height: 40px;
  border: 1px solid ${(props) => props.theme.colors.outline};
  border-radius: 8px;
  background: ${(props) => props.theme.colors.widget_background};
  box-sizing: border-box;

  & input {
    flex: 1;
    min-width: 0;
    height: 100%;
    padding: 0 36px 0 36px;
    border: none;
    background: transparent;
    color: ${(props) => props.theme.colors.white};
    font-size: 14px;

    &::placeholder {
      color: ${(props) => props.theme.colors.inactive};
    }
    &:focus {
      outline: none;
    }
  }
`;

const SearchIconButton = styled.button`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${(props) => props.theme.colors.inactive};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${(props) => props.theme.colors.white};
  }
  &:focus {
    outline: none;
  }
  &:not(:disabled) {
    cursor: pointer;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SearchClearButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${(props) => props.theme.colors.inactive};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    color: ${(props) => props.theme.colors.white};
    background: ${(props) => props.theme.colors.outline};
  }
  &:focus {
    outline: none;
  }
`;

const AbortButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 14px;
  border: none;
  border-radius: 6px;
  background: ${(props) => props.theme.colors.red};
  color: ${(props) => props.theme.colors.white};
  font-size: 13px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  &:focus {
    outline: none;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ExpandButton = styled.button<{ $expanded: boolean }>`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${(props) => props.theme.colors.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  & svg {
    transform: ${(props) => (props.$expanded ? 'rotate(-90deg)' : 'rotate(90deg)')};
    transition: transform 0.2s ease;
  }
  &:focus {
    outline: none;
  }
`;

const ItemBody = styled.div`
  border-top: 1px solid ${(props) => props.theme.colors.outline};
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SearchResultsLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
`;

const TabsList = styled.ol`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  list-style: none;
  margin: 0;
  padding: 0;
  font-size: 13px;
`;

const TabItem = styled.li<{ $active: boolean }>`
  padding: 6px 0;
  cursor: pointer;
  border-bottom: 2px solid
    ${(props) => (props.$active ? props.theme.colors.main_cta : 'transparent')};
  color: ${(props) => (props.$active ? props.theme.colors.main_cta : props.theme.colors.inactive)};
  font-weight: ${(props) => (props.$active ? 'bold' : 'normal')};

  &:hover {
    color: ${(props) => props.theme.colors.text};
  }
`;

const TableWrap = styled.div`
  width: 100%;
  max-height: 280px;
  overflow-y: auto;
  overflow-x: auto;
  box-sizing: border-box;
  padding-right: 2px;

  &::-webkit-scrollbar {
    width: 12px;
  }
  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.background_dashboard};
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.colors.inactive};
    border-radius: 20px;
    border: 3px solid ${(props) => props.theme.colors.background_dashboard};
  }
`;

const TableEmptyMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: 24px;
  font-size: 13px;
  color: ${(props) => props.theme.colors.inactive};
`;

const TABLE_CELL_PADDING = '8px 12px';

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;
`;

const TableHead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 10;
  background: ${(props) => props.theme.colors.widget_background};

  & th {
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    vertical-align: middle;
    color: ${(props) => props.theme.colors.inactive};
    background-color: #323232;
    white-space: nowrap;
    box-sizing: border-box;
    padding: ${TABLE_CELL_PADDING};
  }
`;

const TABLE_ROW_MIN_HEIGHT = '40px';

const TableBody = styled.tbody`
  & td {
    color: ${(props) => props.theme.colors.text};
    box-sizing: border-box;
    padding: ${TABLE_CELL_PADDING};
    vertical-align: middle;
    min-height: ${TABLE_ROW_MIN_HEIGHT};
    height: ${TABLE_ROW_MIN_HEIGHT};
  }

  & tr:last-child td {
    border-bottom: none;
  }
`;

const MessageStatusCell = styled.td`
  color: ${(props) => props.theme.colors.green};
  text-align: left;
`;

const DeviceStatusCell = styled.td<{ $isOffline?: boolean }>`
  color: ${(props) => (props.$isOffline ? props.theme.colors.red : props.theme.colors.text)};
  text-align: left;
`;

const DeviceIdCell = styled.td`
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
  min-height: ${TABLE_ROW_MIN_HEIGHT};
  height: ${TABLE_ROW_MIN_HEIGHT};
`;

const CheckboxCellHead = styled.th`
  width: 40px;
  min-width: 40px;
  max-width: 40px;
  vertical-align: middle;
  box-sizing: border-box;
  padding: 6px 8px;
  text-align: center;
`;

const CheckboxCell = styled.td`
  width: 40px;
  min-width: 40px;
  max-width: 40px;
  vertical-align: middle;
  box-sizing: border-box;
  padding: 6px 8px;
  text-align: center;
`;

export default {
  Section,
  SectionTitle,
  AccordionList,
  AccordionPanel,
  ItemHeader,
  HeaderLeft,
  PlayIconWrap,
  PlayButton,
  HeaderTitleBlock,
  HeaderFilename,
  HeaderSummary,
  HeaderRight,
  SearchWrap,
  SearchInputWrap,
  SearchIconButton,
  SearchClearButton,
  AbortButton,
  ExpandButton,
  ItemBody,
  SearchResultsLabel,
  TabsList,
  TabItem,
  TableWrap,
  TableEmptyMessage,
  Table,
  TableHead,
  TableBody,
  MessageStatusCell,
  DeviceStatusCell,
  DeviceIdCell,
  CheckboxCellHead,
  CheckboxCell,
};
